import { AuthenticationRoot, SignedInRoute, loadSignedInRoute, SignedOutRoute, loadSignedOutRoute, loadRootRoute, loadStudy, loadDemo } from '@/systems/Authentication'
import { SignedOutLayout } from '@/pages/SignedOutLayout'
import { DashboardLayout, FullpageGenericErrorBoundary, DashboardLayoutErrorBoundary, SplashScreen } from '@/DashboardLayout'
import { LoadingBar } from '@/components/DashboardComponents'
import { NewScenarioLayout } from '@/pages/NewScenarioLayout'
import { scenarioSubmit, loadScenario, loadScenarios, loadTrashedScenarios } from '@/data/ScenarioData'
import { NewRubricLayout } from '@/pages/NewRubricLayout'
import { loadRubric, loadRubrics, rubricSubmit } from '@/data/RubricData'
import { createBrowserRouter, createRoutesFromElements, Route, Outlet } from "react-router";
import { RouterProvider } from "react-router/dom";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Scenarios } from '@/pages/Scenarios'
import { Rubrics } from '@/pages/Rubrics'
import { TrashedScenarios } from '@/pages/TrashedScenarios'
import { loadSession, sessionSubmit, previewScenario, previewAssessment, startAssessment } from '@/data/SessionData'
import { SessionLayout } from '@/pages/SessionLayout'
import { Courses, ArchivedCourses } from '@/pages/Courses'
import { useParams } from 'react-router'
import { loadCourses, courseAction, loadCourse, joinCourseViaLink, loadCourseMembers, loadCourseSettings } from '@/data/CoursesData'
import { Course, Assessments, People, CourseSettings } from '@/pages/Course'
import { NewAssessmentLayout } from '@/pages/NewAssessmentLayout'
import { loadAssessment, assessmentSubmitCreate, assessmentSubmitUpdate, loadAssessments } from '@/data/AssessmentData'
import { GraderLayoutFrame, ReviewLayoutFrame, GraderLayoutSubmission } from '@/pages/GraderLayout'
import { loadAssessmentGrader, loadAssessmentStudentSubmission, updateSessionFeedback } from '@/data/GraderData'
import { SettingsLayout, Profile, EmailAndSecurity, OrganizationAccess } from '@/pages/SettingsLayout'
import { loadSettings } from '@/data/ProfileData'
import { ModalProvider } from '@/components/Modal'
import { BlockerProvider } from '@/systems/Blocker'
import { WaitlistLayout } from '@/pages/WaitlistLayout'
import { FeedbackPromptExplorer, loadFeedbackPrompts, createOrUpdateFeedbackPrompt } from '@/pages/FeedbackPromptExplorer'
import { SyncManagerProvider } from '@/systems/DatabaseSync'
import { VerifyEmailLayout, ResetPasswordLayout } from '@/pages/AuthLinkLayouts'
import { Organizations } from '@/pages/Organizations'
import { Organization, OrganizationDepartmentsAndCourses, OrgPeople, OrgSettings } from '@/pages/Organization'
import { loadOrganization, loadSubOrganizationsAndCourses, loadOrgPeople, loadOrgSettings, loadOrganizations } from '@/data/OrganizationData'
import { JoinStudyLayout } from '@/pages/JoinStudyLayout'
import { DemoLayout } from '@/pages/DemoLayout'
import { ResizeProvider } from '@/hooks/useIsMobile'

const queryClient = new QueryClient();

const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route path="" element={<RouterRoot><Outlet/></RouterRoot>} hydrateFallbackElement={<SplashScreen/>} errorElement={<RouterRoot><FullpageGenericErrorBoundary/></RouterRoot>}>
        <Route path="/" element={<SignedInRoute><LoadingBar/><Outlet /></SignedInRoute>} loader={loadSignedInRoute}>
          <Route path="" id="dashboard_root" element={<DashboardLayout><Outlet /></DashboardLayout>} loader={loadCourses} shouldRevalidate={defaultExceptDontRevalidateIfActionFailed}>
            <Route path="" element={<></>} loader={loadRootRoute} errorElement={<DashboardLayoutErrorBoundary/>}></Route>
            <Route path="scenarios" element={<Scenarios/>} loader={loadScenarios} errorElement={<DashboardLayoutErrorBoundary title="Scenarios"/>}></Route>
            <Route path="scenarios/:scenario_id" element={<NewScenarioLayout/>} loader={loadScenario} action={scenarioSubmit} shouldRevalidate={defaultExceptDontRevalidateIfActionFailed}></Route>
            <Route path="scenarios/:scenario_id/preview" element={<></>} loader={previewScenario}></Route>
            <Route path="rubrics" element={<Rubrics/>} loader={loadRubrics} errorElement={<DashboardLayoutErrorBoundary title="Rubrics"/>}></Route>
            <Route path="rubrics/:rubric_id" element={<NewRubricLayout/>} loader={loadRubric} action={rubricSubmit} shouldRevalidate={defaultExceptDontRevalidateIfActionFailed}></Route>
            <Route path="trash" element={<TrashedScenarios/>} loader={loadTrashedScenarios} errorElement={<DashboardLayoutErrorBoundary title="Trash"/>}></Route>
            <Route path="courses" element={<Courses/>} action={courseAction} errorElement={<DashboardLayoutErrorBoundary title="Courses"/>}></Route>
            <Route path="archived-courses" element={<ArchivedCourses/>} errorElement={<DashboardLayoutErrorBoundary title="Archived courses"/>}></Route>
            <Route path="organizations?/:organization_id?/courses/:course_id" id="course" element={<Course/>} loader={loadCourse} shouldRevalidate={defaultExceptDontRevalidateIfActionFailed} errorElement={<DashboardLayoutErrorBoundary title="Course"/>}>
              <Route path="" id="assessments" element={<Assessments/>} loader={loadAssessments}></Route>
              <Route path="people" element={<People/>} loader={loadCourseMembers}></Route>
              <Route path="settings" id="course_settings" element={<CourseSettings/>} loader={loadCourseSettings}></Route>
              <Route path="assessments/:assessment_id/edit" element={<NewAssessmentLayout/>} loader={loadAssessment} action={assessmentSubmitUpdate} shouldRevalidate={defaultExceptDontRevalidateIfActionFailed} errorElement={<FullpageGenericErrorBoundary/>}></Route>
              <Route path="assessments/:assessment_id/edit_scenario" element={<NewScenarioLayout/>} loader={loadScenario} action={scenarioSubmit} shouldRevalidate={defaultExceptDontRevalidateIfActionFailed} errorElement={<FullpageGenericErrorBoundary/>}></Route>
              <Route path="assessments/new/:scenario_id" element={<NewAssessmentLayout is_new={true}/>} action={assessmentSubmitCreate} shouldRevalidate={defaultExceptDontRevalidateIfActionFailed} loader={loadScenario} errorElement={<FullpageGenericErrorBoundary/>}></Route>
              <Route path="assessments/:assessment_id/preview" element={<></>} loader={previewAssessment} errorElement={<FullpageGenericErrorBoundary/>}></Route>
              <Route path="assessments/:assessment_id/start" element={<></>} loader={startAssessment} errorElement={<FullpageGenericErrorBoundary/>}></Route>
              <Route path="assessments/:assessment_id/grader" id="grader" element={<GraderLayoutFrame/>} loader={loadAssessmentGrader} shouldRevalidate={defaultExceptDontRevalidateIfActionFailed} errorElement={<FullpageGenericErrorBoundary/>}>
                <Route path=":student_uid" element={<GraderLayoutSubmission/>} loader={loadAssessmentStudentSubmission} action={updateSessionFeedback} shouldRevalidate={defaultExceptDontRevalidateIfActionFailed} errorElement={<FullpageGenericErrorBoundary/>}></Route>
              </Route>
              <Route path="assessments/:assessment_id/review" element={<ReviewLayoutFrame from="assessment"/>} loader={loadAssessmentStudentSubmission} errorElement={<FullpageGenericErrorBoundary/>} />
            </Route>
            <Route path="organizations" element={<Organizations/>} errorElement={<DashboardLayoutErrorBoundary title="Organizations"/>} loader={loadOrganizations}></Route>
            <Route path="organizations/:organization_id" id="organization" element={<Organization/>} loader={loadOrganization} shouldRevalidate={defaultExceptDontRevalidateIfActionFailed} errorElement={<DashboardLayoutErrorBoundary title="Organization"/>}>
              <Route path="" id="suborganizations" element={<OrganizationDepartmentsAndCourses/>} loader={loadSubOrganizationsAndCourses}/>
              <Route path="people" element={<OrgPeople/>} loader={loadOrgPeople}></Route>
              <Route path="settings" id="orgsettings" element={<OrgSettings/>} loader={loadOrgSettings}></Route>
            </Route>
            <Route path="session/:session_id" element={<SessionLayoutWrapper/>} loader={loadSession} action={sessionSubmit} shouldRevalidate={revalidateUnlessActionWasPerformed}></Route>
            <Route path="session/:session_id/review" element={<ReviewLayoutFrame from="session"/>} loader={loadAssessmentStudentSubmission} action={updateSessionFeedback} shouldRevalidate={defaultExceptDontRevalidateIfActionFailed} errorElement={<FullpageGenericErrorBoundary/>} />
            <Route path="join/:join_code/:assessment_id?" element={<></>} loader={joinCourseViaLink}></Route>
            <Route path="settings" element={<SettingsLayout/>} loader={loadSettings}>
              <Route path="profile" element={<Profile/>}/>
              <Route path="security" element={<EmailAndSecurity/>}/>
              <Route path="organizations" element={<OrganizationAccess/>}/>
            </Route>
            <Route path="feedback_prompt_manager" element={<FeedbackPromptExplorer/>} loader={loadFeedbackPrompts} errorElement={<FullpageGenericErrorBoundary/>} action={createOrUpdateFeedbackPrompt} shouldRevalidate={defaultExceptDontRevalidateIfActionFailed}></Route>
          </Route>
        </Route>
        <Route path="/login" element={<SignedOutRoute><SignedOutLayout /></SignedOutRoute>} loader={loadSignedOutRoute}></Route>
        <Route path="/study/:join_code" element={<JoinStudyLayout/>} loader={loadStudy} errorElement={<FullpageGenericErrorBoundary/>}></Route>
        <Route path="/demo/:join_code/:assessment_id" element={<DemoLayout/>} loader={loadDemo} errorElement={<FullpageGenericErrorBoundary/>}></Route>
        {/* <Route path="/waitlist" element={<WaitlistLayout/>}></Route> */}
        <Route path="/verify/:oobCode" element={<VerifyEmailLayout/>}></Route>
        <Route path="/reset/:oobCode" element={<ResetPasswordLayout/>}></Route>
      </Route>
    </>
  )
)

function SessionLayoutWrapper() {
  const { session_id } = useParams();
  return <SessionLayout key={session_id} />;
}

function revalidateUnlessActionFailed({actionResult}) {
  const failed = actionResult?.success === false;

  return !failed;
}

function defaultExceptDontRevalidateIfActionFailed({actionResult, defaultShouldRevalidate}) {
  const failed = actionResult?.success === false;

  if (failed) {
    return false;
  } else {
    return defaultShouldRevalidate;
  }
}

function revalidateUnlessActionWasPerformed({actionResult}) {
  return !actionResult;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthenticationRoot>
        <SyncManagerProvider>
          <RouterProvider router={router} />
        </SyncManagerProvider>
      </AuthenticationRoot>
    </QueryClientProvider>
  )
}

function RouterRoot({ children }) {
  return (
    <BlockerProvider>
      <ResizeProvider>
        <ModalProvider>
          {children}
        </ModalProvider>
      </ResizeProvider>
    </BlockerProvider>
  )
}